import { lazy, Suspense } from "react";
// React router imports
import { BrowserRouter, Routes, Route } from "react-router-dom";
// Core component imports (always loaded)
import Toc from "./components/Toc";
import Settings from "./components/Forms/Settings";
// import { UserContext } from "./components/Auth";
// Prompts moved to lazy-loaded admin components
import Demo from "./components/Chat/Demo.jsx";
import LoadingVisualization from "./components/LoadingVisualization";
import AppLayout from "./components/layout/AppLayout";
// Lazy-loaded admin components (code-split)
const UserGrid = lazy(() => import("./components/Grids/UserGrid"));
const UserGroupsGrid = lazy(() => import("./components/Grids/UserGroups"));
const UserGroupDetail = lazy(() => import("./components/Grids/UserGroupDetail"));
const Users2GroupsGrid = lazy(() => import("./components/Grids/Users2Groups"));
const User = lazy(() => import("./components/Forms/User"));
const CompanyGrid = lazy(() => import("./components/Grids/CompanyGrid"));
const Company = lazy(() => import("./components/Forms/Company"));
const StoreGrid = lazy(() => import("./components/Grids/StoreGrid"));
const Store = lazy(() => import("./components/Forms/Store"));
const EmployeeGrid = lazy(() => import("./components/Grids/EmployeeGrid"));
const Employee = lazy(() => import("./components/Forms/Employees"));
const HoursGrid = lazy(() => import("./components/Grids/HoursGrid"));
const Z1G1Chat = lazy(() => import("./components/Chat/Z1G1Chat"));
const ReportCustomizer = lazy(() => import("./components/Reports/ReportCustomizer"));
const Prompts = lazy(() => import("./components/Prompts"));
const AdminDashboard = lazy(() => import("./components/Dashboard/AdminDashboard"));
const NotebooksGrid = lazy(() => import("./components/Notebooks/NotebooksGrid"));
const NotebookViewer = lazy(() => import("./components/Notebooks/NotebookViewerAlternative"));
const Forecast = lazy(() => import("./components/Forms/Forecast"));
// Inventory components
const Inventory = lazy(() => import("./components/Inventory/Inventory"));
const ProductsGrid = lazy(() => import("./components/Grids/ProductsGrid"));
const SuppliersGrid = lazy(() => import("./components/Grids/SuppliersGrid"));
const TagsGrid = lazy(() => import("./components/Grids/TagsGrid"));
const ProductDetailPage = lazy(() => import("./components/Grids/ProductDetailPage"));
const SupplierDetailPage = lazy(() => import("./components/Grids/SupplierDetailPage"));
const TagDetailPage = lazy(() => import("./components/Grids/TagDetailPage"));
// New components for the updated architecture
import LoginPage from "./components/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import RootRedirect from "./components/RootRedirect";
// Admin route wrapper with loading state
const AdminRoute = ({ children }) => (
  <Suspense fallback={<LoadingVisualization />}>
    {children}
  </Suspense>
);
// Routes component without router wrapper (for testing)
function ZRoutesContent() {
  // const { user, originalUser, isImpersonating } = useContext(UserContext);
  return (
    <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/demo/" element={<Demo />} />
        {/* Root route - redirect based on authentication state */}
        <Route path="/" element={<RootRedirect />} />
        {/* Protected Routes - Authentication required */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingVisualization />}>
              <AppLayout>
                <Z1G1Chat />
              </AppLayout>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/toc" element={
          <ProtectedRoute adminOnly={true}>
            <AppLayout>
              <Toc />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute adminOnly={true}>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/prompts" element={
          <ProtectedRoute adminOnly={true}>
            <AppLayout>
              <Prompts />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Admin-only routes - Code-split for better performance */}
        {/* Admin Dashboard */}
        <Route path="/dashboard" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                <Suspense fallback={<div>Loading Dashboard...</div>}>
                  <AdminDashboard />
                </Suspense>
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Store Management Routes */}
        <Route path="/stores" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <StoreGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/stores/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <Store />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Employee Management Routes */}
        <Route path="/hours" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <HoursGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/employees" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <EmployeeGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/employees/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <Employee />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* User Management Routes */}
        <Route path="/users" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <UserGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/users/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <User />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/usergroups" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <UserGroupsGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/usergroups/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <UserGroupDetail />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/users2groups" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <Users2GroupsGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Company Management Routes */}
        <Route path="/companies" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <CompanyGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/companies/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <Company />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Reporting Routes */}
        <Route path="/reports" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <ReportCustomizer />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Forecast Routes */}
        <Route path="/forecast" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <Forecast />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Notebook Routes */}
        <Route path="/notebooks" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <NotebooksGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/notebooks/:notebookId" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <NotebookViewer />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        {/* Inventory Routes */}
        <Route path="/inventory" element={
          <ProtectedRoute adminOnly={true}>
            <Suspense fallback={<LoadingVisualization />}>
              <AppLayout>
                <Inventory />
              </AppLayout>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/inventory/:tab" element={
          <ProtectedRoute adminOnly={true}>
            <Suspense fallback={<LoadingVisualization />}>
              <AppLayout>
                <Inventory />
              </AppLayout>
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Inventory Data Grids */}
        <Route path="/products" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <ProductsGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/products/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <ProductDetailPage />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/suppliers" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <SuppliersGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/suppliers/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <SupplierDetailPage />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/tags" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <TagsGrid />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/tags/:id" element={
          <ProtectedRoute adminOnly={true}>
            <AdminRoute>
              <AppLayout>
                  <TagDetailPage />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
    </Routes>
  );
}
// Main routes component with BrowserRouter (for production)
function ZRoutes() {
  return (
    <BrowserRouter>
      <ZRoutesContent />
    </BrowserRouter>
  );
}
export default ZRoutes;
export { ZRoutesContent };
